import React, { useEffect, useRef, useState } from "react";
import "./MyAppointments.css";

function formatMoney(amount) {
  return `$${Number(amount || 0).toFixed(2)}`;
}

function formatDate(dateString) {
  if (!dateString) return "Date not available";

  // Use the date portion to avoid timezone shifts.
  const dateOnly = dateString.split("T")[0];
  const [year, month, day] = dateOnly.split("-").map(Number);

  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatStatus(status) {
  if (!status) return "Unknown";

  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getSelectionLabel(key) {
  const labels = {
    color: "Color",
    customColor: "Custom color",
    length: "Length",
    size: "Size",
    braidCount: "Number of braids",
    hairWash: "Hair wash",
  };

  return labels[key] || key;
}

function formatSelectionValue(key, value) {
  if (key === "hairWash") {
    return value ? "Yes" : "No";
  }

  if (key === "braidCount") {
    return `${value} braids`;
  }

  return String(value)
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function MyAppointments() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email");
  const [appointments, setAppointments] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Cloudflare Turnstile state
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileError, setTurnstileError] = useState("");

  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  // Initialize Turnstile once and keep its container mounted
  // while switching between the email and verification-code steps.
  useEffect(() => {
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

    if (!siteKey) {
      setTurnstileError("Security verification is not configured.");
      console.error("Turnstile site key is missing.");
      return undefined;
    }

    let cancelled = false;
    let script = null;

    function handleScriptError() {
      if (cancelled) return;

      setTurnstileError(
        "Unable to load security verification. Check your connection and refresh the page."
      );
    }

    const renderWidget = () => {
      if (
        cancelled ||
        !window.turnstile ||
        !turnstileRef.current ||
        turnstileWidgetId.current !== null
      ) {
        return;
      }

      try {
        turnstileWidgetId.current = window.turnstile.render(
          turnstileRef.current,
          {
            sitekey: siteKey,
            theme: "light",
            size: "flexible",

            callback: (token) => {
              setTurnstileToken(token);
              setTurnstileError("");
            },

            "expired-callback": () => {
              setTurnstileToken("");
            },

            "error-callback": () => {
              setTurnstileToken("");
              setTurnstileError(
                "Security verification could not be completed. Please try again."
              );
            },
          }
        );

        setTurnstileReady(true);
        setTurnstileError("");
      } catch (error) {
        console.error("Unable to render Turnstile:", error);

        setTurnstileError(
          "Unable to load security verification. Please refresh and try again."
        );
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      // Reuse an existing Turnstile script if one is already present.
      script = document.querySelector(
        'script[src*="challenges.cloudflare.com/turnstile"]'
      );

      if (!script) {
        script = document.createElement("script");

        script.src =
          "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

        script.async = true;
        script.defer = true;

        document.head.appendChild(script);
      }

      script.addEventListener("load", renderWidget);
      script.addEventListener("error", handleScriptError);
    }

    return () => {
      cancelled = true;

      if (script) {
        script.removeEventListener("load", renderWidget);
        script.removeEventListener("error", handleScriptError);
      }

      if (
        turnstileWidgetId.current !== null &&
        window.turnstile
      ) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }

      setTurnstileReady(false);
    };
  }, []);

  // Clear the old token and request a fresh Turnstile challenge.
  const resetTurnstile = () => {
    setTurnstileToken("");

    if (
      window.turnstile &&
      turnstileWidgetId.current !== null
    ) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch (error) {
        console.error("Unable to reset Turnstile:", error);
      }
    }
  };

  // Request the email verification code.
  const handleRequestCode = async (event) => {
    event.preventDefault();

    if (!turnstileToken) {
      setMessage("Please complete the security verification.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await fetch("/api/appointments/request-code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          turnstileToken,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message || "Unable to request a verification code."
        );
      }

      setStep("code");

      setMessage(
        result.message ||
          "If an appointment is associated with that email address, a verification code will be sent."
      );
    } catch (error) {
      setMessage(
        error.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
      resetTurnstile();
    }
  };

  // Verify the code and retrieve the customer's appointments.
  const handleVerifyCode = async (event) => {
    event.preventDefault();

    if (!turnstileToken) {
      setMessage("Please complete the security verification.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await fetch("/api/appointments/verify-code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          code: code.trim(),
          turnstileToken,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message || "The verification code is invalid or expired."
        );
      }

      setAppointments(result.appointments || []);
      setStep("appointments");
      setMessage("");
    } catch (error) {
      setMessage(
        error.message || "Unable to verify your code. Please try again."
      );
    } finally {
      setLoading(false);
      resetTurnstile();
    }
  };

  // Return to the email screen and reset the form.
  const handleStartOver = () => {
    if (loading) return;

    setEmail("");
    setCode("");
    setAppointments([]);
    setMessage("");

    resetTurnstile();

    setStep("email");
  };

  return (
    <main className="my-appointments-page">
      <section className="my-appointments-container">
        <h1>My Appointments</h1>

        {/* One persistent Turnstile widget for both form steps. */}
        <div
          className="turnstile-container"
          style={{
            display: step === "appointments" ? "none" : "block",
          }}
          aria-live="polite"
        >
          <div ref={turnstileRef}></div>

          {!turnstileReady && !turnstileError && (
            <p>Loading security verification...</p>
          )}

          {turnstileError && (
            <p className="turnstile-error" role="alert">
              {turnstileError}
            </p>
          )}
        </div>

        {/* EMAIL STEP */}
        {step === "email" && (
          <>
            <p>
              Enter the email address you used when requesting your
              appointment. We'll send you a verification code so you can
              securely review your bookings.
            </p>

            <form onSubmit={handleRequestCode}>
              <label htmlFor="appointment-email">
                Email address
              </label>

              <input
                id="appointment-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                maxLength={254}
                required
              />

              <button
                type="submit"
                className="button"
                disabled={
                  loading ||
                  !turnstileReady ||
                  !turnstileToken
                }
              >
                {loading
                  ? "Sending..."
                  : "Send verification code"}
              </button>
            </form>
          </>
        )}

        {/* VERIFICATION CODE STEP */}
        {step === "code" && (
          <>
            <p>
              Enter the six-digit verification code sent to your email
              address. The code expires after 10 minutes.
            </p>

            <form onSubmit={handleVerifyCode}>
              <label htmlFor="appointment-code">
                Verification code
              </label>

              <input
                id="appointment-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(event) =>
                  setCode(
                    event.target.value
                      .replace(/\D/g, "")
                      .slice(0, 6)
                  )
                }
                placeholder="Enter 6-digit code"
                autoComplete="one-time-code"
                required
              />

              <button
                type="submit"
                className="button"
                disabled={
                  loading ||
                  code.length !== 6 ||
                  !turnstileReady ||
                  !turnstileToken
                }
              >
                {loading ? "Verifying..." : "Verify code"}
              </button>
            </form>

            <button
              type="button"
              className="button"
              onClick={handleStartOver}
              disabled={loading}
            >
              Use a different email
            </button>
          </>
        )}

        {/* APPOINTMENTS RESULTS STEP */}
        {step === "appointments" && (
          <div className="appointments-results">
            <p>
              Appointments associated with{" "}
              <strong>{email}</strong>
            </p>

            {appointments.length === 0 ? (
              <div className="appointments-empty">
                <h2>No appointments found</h2>

                <p>
                  We couldn't find any appointments associated with this
                  email address. If you recently requested an appointment,
                  please contact us for help.
                </p>
              </div>
            ) : (
              appointments.map((appointment) => (
                <article
                  className="appointment-card"
                  key={appointment.confirmationNumber}
                >
                  <div className="appointment-card-header">
                    <div>
                      <span className="appointment-eyebrow">
                        YOUR APPOINTMENT
                      </span>

                      <h2>
                        {appointment.braid?.name || "Appointment"}
                      </h2>
                    </div>

                    <span
                      className={`status-badge status-${appointment.bookingStatus}`}
                    >
                      {formatStatus(appointment.bookingStatus)}
                    </span>
                  </div>

                  <div className="appointment-confirmation">
                    <span>Confirmation number</span>
                    <strong>
                      {appointment.confirmationNumber}
                    </strong>
                  </div>

                  <div className="appointment-details-grid">
                    <div className="appointment-detail">
                      <span>Date</span>

                      <strong>
                        {formatDate(
                          appointment.appointment?.requestedDate
                        )}
                      </strong>
                    </div>

                    <div className="appointment-detail">
                      <span>Time</span>

                      <strong>
                        {appointment.appointment?.requestedTime ||
                          "Not available"}
                      </strong>
                    </div>
                  </div>

                  {appointment.selections &&
                    Object.keys(appointment.selections).length > 0 && (
                      <div className="appointment-section">
                        <h3>Your selections</h3>

                        <div className="appointment-selections-grid">
                          {Object.entries(appointment.selections)
                            .filter(
                              ([, value]) =>
                                value !== undefined &&
                                value !== null &&
                                value !== ""
                            )
                            .map(([key, value]) => (
                              <div
                                className="selection-item"
                                key={key}
                              >
                                <span>
                                  {getSelectionLabel(key)}
                                </span>

                                <strong>
                                  {formatSelectionValue(key, value)}
                                </strong>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {appointment.pricing && (
                    <div className="appointment-section">
                      <h3>Payment summary</h3>

                      <div className="appointment-price-row">
                        <span>Total price</span>

                        <strong>
                          {formatMoney(appointment.pricing.total)}
                        </strong>
                      </div>

                      <div className="appointment-price-row">
                        <span>Deposit</span>

                        <strong>
                          {formatMoney(appointment.pricing.deposit)}
                        </strong>
                      </div>

                      <div className="appointment-price-row">
                        <span>Remaining balance</span>

                        <strong>
                          {formatMoney(appointment.pricing.balance)}
                        </strong>
                      </div>
                    </div>
                  )}

                  <div className="appointment-card-footer">
                    <div>
                      <span>Deposit status</span>

                      <strong
                        className={`deposit-status deposit-${appointment.depositStatus}`}
                      >
                        {formatStatus(appointment.depositStatus)}
                      </strong>
                    </div>
                  </div>
                </article>
              ))
            )}

            <button
              type="button"
              className="button"
              onClick={handleStartOver}
            >
              Done
            </button>
          </div>
        )}

        {message && (
          <p role="status" aria-live="polite">
            {message}
          </p>
        )}
      </section>
    </main>
  );
}

export default MyAppointments;